import { Component, OnInit, Input, Output, EventEmitter, inject } from "@angular/core";
import { FormGroup, FormControl, AbstractControl, ReactiveFormsModule } from "@angular/forms";
import { getFormControl } from "@methods/CommonMethods";
import { BsModalService, ModalOptions } from "ngx-bootstrap/modal";
import { DataStructuresStandingData } from "@app/datamanagement/models/DataStructuresStandingData";
import { IdNameCombination } from "@models/generic/IdNameCombination";
import { EntityModel } from "@app/datamanagement/models/EntityModel";
import { SystemsDialogComponent } from "../../dialogs/systems-dialog/systems-dialog.component";
import { EntityRoles } from "@app/datamanagement/enums/EntityRoles";
import { SystemModel } from "@app/datamanagement/models/SystemModel";
import { FormValidationHelper } from "@app/shared/helpers/form-validation-helper";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { injectTranslation, provideTranslationScope, TranslatePipe } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/datamanagement/i18n/scope";
import { NgSelectModule } from "@ng-select/ng-select";
import { CheckboxComponent } from "@app/shared/cerrix-checkbox/cerrix-checkbox.component";
import { SystemDetailComponent } from "../../systems/system-detail/system-detail.component";

@Component({
    selector: "entity-detail",
    templateUrl: "./entity-detail.component.html",
    styleUrls: ["./entity-detail.component.scss", "../../../../sass/datamanagement.scss"],
    imports: [
        TranslatePipe,
        ReactiveFormsModule,
        NgSelectModule,
        CheckboxComponent,
        SystemDetailComponent,
    ],
    viewProviders: [provideTranslationScope(scope)],
})
export class EntityDetailComponent implements OnInit {
    private _modalService = inject(BsModalService);
    private _promptService = inject(CerrixPromptService);
    private _translation = injectTranslation(scope);

    @Input() entityModel: EntityModel;
    @Input() standingData: DataStructuresStandingData;
    @Input() disabled: boolean;
    @Output() entityDeleted = new EventEmitter<EntityModel>();

    entityForm: FormGroup;

    entityRoleSelected = false;
    showSubprocessor = false;

    systemsRequiredSelected = false;
    showAddSystems = false;

    entityRoles: any[];
    systemsRequired: any[];
    systemOptions: IdNameCombination[];
    selectedSystems: IdNameCombination[];

    async ngOnInit() {
        // t() needs the scope loaded first; before ready() it throws in development.
        await this._translation.ready();

        // Get the entity info from the standing data
        const entityInfo = this.standingData.thirdparties.find((x) => x.ID === this.entityModel.id);

        const isNew = this.entityModel.isNew;

        const hasDataControllerFlag =
            EntityRoles.DataController === (entityInfo.EntityRoles & EntityRoles.DataController);
        const hasDataProcessorFlag =
            EntityRoles.DataProcessor === (entityInfo.EntityRoles & EntityRoles.DataProcessor);

        if (isNew) {
            if (hasDataControllerFlag && !hasDataProcessorFlag) {
                this.entityModel.role = EntityRoles.DataController;
            } else {
                this.entityModel.role = EntityRoles.DataProcessor;
            }
        }
        this.initDropDowns(hasDataControllerFlag, hasDataProcessorFlag);
        this.initFormValidation();
        this.loadSystems();

        this.changeRole(this.entityModel.role);

        if (!isNew) {
            this.changeSystemsRequired(this.entityModel.systemsRequired, null);
        }
    }

    initDropDowns(hasControllerFlag: boolean, hasProcessorFlag: boolean) {
        const entityRoles = [];
        if (hasControllerFlag) {
            entityRoles.push({
                id: EntityRoles.DataController,
                name: this._translation.t("dataStructures.dataControllerRole"),
            });
        }

        if (hasProcessorFlag) {
            entityRoles.push({
                id: EntityRoles.DataProcessor,
                name: this._translation.t("dataStructures.processorRole"),
            });
        }

        this.entityRoles = entityRoles;

        this.systemsRequired = [
            {
                id: true,
                name: this._translation.t("dataStructures.yesOption"),
            },
            {
                id: false,
                name: this._translation.t("dataStructures.noOption"),
            },
        ];
    }

    initFormValidation() {
        this.entityForm = new FormGroup({
            role: new FormControl(this.entityModel.role),
            subProcessor: new FormControl(this.entityModel.subProcessor),
            systemsRequired: new FormControl(this.entityModel.systemsRequired),
        });

        if (this.disabled) {
            this.entityForm.disable();
        } else {
            // Only bind the change listener when the entity has more than one role
            const roleControl = getFormControl<EntityModel>(this.entityForm, "role");
            if (this.entityRoles.length > 1) {
                roleControl.valueChanges.subscribe((roleValue: number) => {
                    this.changeRole(roleValue);
                });
            } else {
                roleControl.disable();
            }

            const systemsRequiredControl = getFormControl<EntityModel>(
                this.entityForm,
                "systemsRequired"
            );
            systemsRequiredControl.valueChanges.subscribe((required: boolean) => {
                this.changeSystemsRequired(required, systemsRequiredControl);
            });
        }
    }

    changeRole(role: number) {
        if (!role) {
            return;
        }

        this.entityRoleSelected = true;
        this.showSubprocessor = role === EntityRoles.DataProcessor;
    }

    async changeSystemsRequired(systemsRequired: boolean, systemsRequiredControl: AbstractControl) {
        this.systemsRequiredSelected = true;

        if (systemsRequiredControl) {
            const hasSystems = this.entityModel.systems.length > 0;
            // If systems are connected and systems are not required, ask user for confirmation
            if (
                !systemsRequired &&
                hasSystems &&
                !(await this._promptService
                    .confirm(
                        this._translation.t("dataStructures.removeSystemsTitle"),
                        this._translation.t("dataStructures.removeSystemsBody")
                    )
                    .toPromise())
            ) {
                // Set value back to true
                systemsRequiredControl.setValue(true, { emitEvent: false });

                return;
            }

            // Check the hidden system
            if (
                systemsRequired &&
                hasSystems &&
                this.entityModel.systems[0].dataSubjects.length > 0 &&
                !(await this._promptService
                    .confirm(
                        this._translation.t("dataStructures.removeDataSubjectsTitle"),
                        this._translation.t("dataStructures.removeDataSubjectsBody")
                    )
                    .toPromise())
            ) {
                // Set value back to false
                systemsRequiredControl.setValue(false, { emitEvent: false });

                return;
            }

            // Add hidden systems to the entity
            if (!systemsRequired) {
                const hiddenSystem = <SystemModel>{
                    id: 0,
                    hidden: true,
                    dataSubjects: [],
                    dataSubjectIds: [],
                    dataSubjectsLoaded: true,
                };

                this.entityModel.systems = [hiddenSystem];
            } else {
                // Clear all systems from the entityModel
                this.entityModel.systems = [];
            }
        }

        this.showAddSystems = systemsRequired === true;
    }

    loadSystems() {
        if (!this.entityModel || !this.entityModel.id) {
            return;
        }

        // Get available systems for the entity from the standing data
        const sdEntity = this.standingData.thirdparties.filter((x) => x.ID === this.entityModel.id);
        if (sdEntity.length > 0) {
            this.systemOptions = sdEntity[0].Systems;
        } else {
            this.systemOptions = [];
        }
    }

    showAddSystemsDialog() {
        // Only show systems which have not been chosen for this entity
        const currentSystemIds = this.entityModel.systems.map((x) => x.id);

        const availableSystems = this.systemOptions.filter(
            (x) => currentSystemIds.indexOf(x.ID) === -1
        );
        const systemsClone = JSON.parse(JSON.stringify(availableSystems)) as IdNameCombination[];

        const config = <ModalOptions<SystemsDialogComponent>>{
            backdrop: "static",
            class: "datamanagement-dialog modal-md modal-lg",

            initialState: <SystemsDialogComponent>{
                entityModel: this.entityModel,
                systemOptions: systemsClone,
                standingData: this.standingData,
            },
        };

        this._modalService
            .show(SystemsDialogComponent, config)
            .content.afterClosed.subscribe((dialogResult: SystemModel[] | boolean) => {
                if (dialogResult) {
                    const addedSystems = dialogResult as SystemModel[];
                    this.entityModel.systems.addRange(addedSystems);
                }
            });
    }

    deleteSystem(systemModel: SystemModel) {
        const index = this.entityModel.systems.indexOf(systemModel);
        if (index >= 0) {
            this.entityModel.systems.splice(index, 1);
        }
    }

    // endregion

    deleteEntity() {
        this._promptService
            .confirm(
                this._translation.t("dataStructures.deleteEntityTitle"),
                this._translation.t("dataStructures.deleteEntityBody")
            )
            .onConfirm()
            .subscribe(() => {
                this.entityDeleted.emit(this.entityModel);
            });
    }

    mapToModel() {
        FormValidationHelper.mapToModel(this.entityForm, this.entityModel);
    }
}
